import { computed, effect, signal } from '@preact/signals-react'
import type { GameRegistry } from '../../catalog/gameRegistry/gameRegistry.types'
import type { GameCatalogDep } from '../../catalog/gameCatalog/gameCatalog.types'
import type { RoomDep } from '../../room/room.types'
import { normalizeHostPlays, seatEligibleMates, type GameSavedOptions } from '@merels/room'
import type { LocalMatesSystem } from '../../localMates/localMates.types'
import type { Mode } from '../../mode/mode/mode.types'
import type { ControllerMates } from '../../localMates/controllerMates/controllerMates.types'
import type { PlayerAssignmentPreferences } from '../../matesToPlayers/playerAssignmentPreferences/playerAssignmentPreferences.types'
import type { GameOptionsDialogDep } from './gameOptionsDialog/gameOptionsDialog.types'
import type { DebugPanelVM } from '../debugPanelVM/debugPanelVM.types'
import type { Bot } from '../../bot/bot/bot.types'
import type { ControllerInput } from '@merels/input/controllerInput'
import type { FocusNavigation } from '../../mode/focusNavigation/focusNavigation.types'
import { getOrGenerateClientId } from '../../utils/clientId'
import type { AuthDep } from '../../auth/auth.types'
import type { GameCurationVM } from '../gameCurationVM/gameCurationVM.types'
import type { GameCatalogGame } from '../../catalog/gameCatalog/gameCatalog.types'
import { arrangeGameTiles } from './arrangeGameTiles'
import { hostTermInSentence } from '../../utils/hostTerm'
import { makeNetworkedSimBinding } from '../../game/realtimeNetworkedSim/networkedSimBinding'
import type { NetworkedSimBinding } from '../../game/realtimeNetworkedSim/networkedSimBinding.types'
import type {
  GameNotice,
  PlayAreaVMDep,
  RoomViewVMNoActiveGameTileVM,
  StartGameSetupPhase,
} from './playAreaVM.types'

export type PlayAreaVMDeps = {
  roomState: RoomDep['state']
  roomConnection: RoomDep['connection']
  roomActions: RoomDep['actions']
  localMatesSystem: LocalMatesSystem
  mode: Mode
  focusNavigation: FocusNavigation
  controllerInput: ControllerInput
  controllerMates: ControllerMates
  playerAssignmentPreferences: PlayerAssignmentPreferences
  gameOptionsDialog: GameOptionsDialogDep
  debugPanelVM: DebugPanelVM
  gameRegistry: GameRegistry
  gameCatalog: GameCatalogDep
  bot: Bot
  auth: AuthDep
  gameCuration: GameCurationVM
}

function parseDirectionFromRawEvent(rawEvent: string): 'up' | 'down' | 'left' | 'right' | null {
  if (rawEvent.includes('ArrowUp')) return 'up'
  if (rawEvent.includes('ArrowDown')) return 'down'
  if (rawEvent.includes('ArrowLeft')) return 'left'
  if (rawEvent.includes('ArrowRight')) return 'right'

  if (rawEvent.includes('D-pad Up')) return 'up'
  if (rawEvent.includes('D-pad Down')) return 'down'
  if (rawEvent.includes('D-pad Left')) return 'left'
  if (rawEvent.includes('D-pad Right')) return 'right'

  if (rawEvent.includes('Left stick up')) return 'up'
  if (rawEvent.includes('Left stick down')) return 'down'
  if (rawEvent.includes('Left stick left')) return 'left'
  if (rawEvent.includes('Left stick right')) return 'right'

  return null
}

/**
 * Resolve mateId for a controller device (e.g. X1, X2) in shared/Omni mode.
 * Tries room mates first, then local mates as fallback (e.g. before room sync).
 */
function getMateIdForControllerDeviceId(
  roomMates: any[],
  localMates: any[],
  deviceId: string
): string | undefined {
  const localClientId = getOrGenerateClientId()

  // Find mate by controllerId in a mates list (room or local); returns undefined if not found.
  const fromRoom = (mates: any[]) =>
    (mates || []).find((m: any) => {
      if (!m || typeof m.clientId !== 'string') return false
      if (localClientId && m.clientId !== localClientId) return false
      return (m.type === 'shared' || m.type === 'omni') && m.controllerId === deviceId
    })

  const mate = fromRoom(roomMates) ?? fromRoom(localMates)
  if (!mate) return undefined
  const mateId = (mate as any).mateId
  return typeof mateId === 'string' ? mateId : undefined
}

export function makePlayAreaVMDep(deps: PlayAreaVMDeps): PlayAreaVMDep {
  const {
    roomState,
    roomConnection,
    roomActions,
    localMatesSystem,
    mode,
    focusNavigation,
    controllerInput,
    controllerMates,
    gameOptionsDialog,
    debugPanelVM,
    gameRegistry,
    gameCatalog,
    bot,
    auth,
    gameCuration,
  } = deps

  const activeGame = computed(() => roomState.activeGame.value ?? null)
  const gameSetup = computed(() => roomState.gameSetup.value ?? null)
  const error = roomConnection.error

  const isSharedScreen = computed(() => mode.computedMode.value.sharedScreen)
  const isOmniMode = computed(() => mode.computedMode.value.omniMode)
  const isLocalMode = computed(() => mode.isLocalMode())
  // Networked, multi-client room (as opposed to local/shared-screen) — see
  // GameProps.isNetworked. Used to gate the realtime networked sim binding
  // below (a realtime game must not run its own local sim outside a real
  // networked room; GameContainer's realtimeLoop handles shared-screen).
  const isNetworked = computed(() => !isLocalMode.value)

  const viewingMateId = computed<string | undefined>(() => {
    // Room-wide (not just this client's own isOmniMode toggle) - any connected
    // client may act as, and submit moves for, any Omni mate in the room (see
    // activeOmniMate, sourced room-wide in room/init.ts), not just the client
    // that turned Omni mode on.
    if (roomState.hasOmniMates.value) {
      return localMatesSystem.omniMates.activeOmniMate.activeLocalOmniMateId.value
    }

    // Shared-screen mode: viewing mate is undefined (we don't attribute pointer/keyboard to a mate).
    if (isSharedScreen.value) {
      return undefined
    }

    // Otherwise: use the first local mate (should be the only one in non-test, non-shared mode).
    return localMatesSystem.localMates.localMates.value[0]?.mateId
  })

  /**
   * The player slot index (0-based) that this client is viewing as.
   * Derived from viewingMateId and the active game's playerToMateIds mapping.
   */
  const viewingPlayerIndex = computed<number | undefined>(() => {
    const mateId = viewingMateId.value
    const game = activeGame.value
    if (!mateId || !game) return undefined

    const playerToMateIds = game.playerToMateIds
    if (!Array.isArray(playerToMateIds)) return undefined

    const idx = playerToMateIds.findIndex(
      (mateIds: string[]) => Array.isArray(mateIds) && mateIds.includes(mateId)
    )
    return idx >= 0 ? idx : undefined
  })

  /**
   * How many mates the room would seat in a game: the same rule the room
   * applies (see @merels/room's seatEligibleMates), so a tile is never
   * offered for a game the room would refuse. Hosts who observe under the
   * game's Host plays setting don't count, and while the room has Omni mates
   * only they (and bots) do. Room-wide, so every client sees the same tiles.
   */
  function seatableMateCount(gameId?: string): number {
    // When a game is active we don't show the game list UI, so the count is irrelevant.
    if (activeGame.value) return 0
    // Local shared-screen mates are controllers on this device, not yet in the room.
    if (isSharedScreen.value && !roomState.hasOmniMates.value && (isLocalMode.value || roomState.mates.value.length === 0)) {
      return controllerMates.mates.value.length
    }
    const mates = (roomState.mates.value || []).filter((m: any) => !String(m.mateId).endsWith('_placeholder'))
    const hostPlays = (gameId && roomState.hostPlaysByGameId.value[gameId]) || normalizeHostPlays(undefined)
    const room = { mates, hostMateIds: roomState.hostMateIds.value, managed: roomState.managed.value }
    return seatEligibleMates(room, hostPlays).length
  }

  /** Seat-eligible mates under the default Host plays setting. */
  const mateCount = computed(() => seatableMateCount())

  // ===== Start-game setup (shared state) =====

  const startSetup = computed(() => roomState.gameSetup.value ?? null)
  // While the room has any Omni mate, seating is always automatic (see
  // debugPanelVM.ts's auto-seat effect) - manual arrangement is never the right
  // UI there, even for the brief moment between the setup appearing and that
  // effect resolving it, so never show the dialog in that case.
  const startSetupOpen = computed(() => Boolean(startSetup.value) && !roomState.hasOmniMates.value)

  // ===== Pre-game configure dialog (shared state) =====

  const configureGameId = signal<string | null>(null)
  const configureOpen = signal<boolean>(false)
  /** Fetched saved options when opening configure from catalog (no game setup). */
  const preGameConfigureSavedOptions = signal<GameSavedOptions | null>(null)

  function openConfigure(gameId: string): void {
    configureGameId.value = gameId
    configureOpen.value = true
    preGameConfigureSavedOptions.value = null
  }

  function closeConfigure(): void {
    configureOpen.value = false
    configureGameId.value = null
    preGameConfigureSavedOptions.value = null
  }

  // Fetch saved options when opening configure from catalog (no game in setup).
  effect(() => {
    const gameId = configureGameId.value
    const isOpen = configureOpen.value
    const setupGameId = roomState.gameSetup.value?.gameId

    if (!isOpen || !gameId) return
    if (setupGameId === gameId) return // In setup: roomState.savedOptions is used

    void roomActions.getSavedGameOptions(gameId).then((opts) => {
      if (configureGameId.value === gameId && configureOpen.value) {
        preGameConfigureSavedOptions.value = opts
      }
    })
  })

  // ===== NoActiveGameUI =====

  const incompatibleGameIds = signal<Set<string>>(new Set())
  /** A dialog explaining why a game can't be opened, such as an incompatible version or too few players. */
  const gameNotice = signal<GameNotice | null>(null)

  function incompatibleGameNotice(displayName: string): GameNotice {
    return {
      title: 'Game version is incompatible',
      message: `${displayName} was published with a game API version that this Merels platform does not support. Ask its developer to publish a compatible version, then select that version for this channel.`,
    }
  }

  const catalogTiles = computed<RoomViewVMNoActiveGameTileVM[]>(() => {
    const inSharedScreen = isSharedScreen.value

    const user = auth.currentUser.value
    const isRoomOwner = user ? roomState.ownerId.value === user.id : false
    const isLocal = mode.isLocalMode()

    const list: RoomViewVMNoActiveGameTileVM[] = []

    if (isRoomOwner || isLocal) {
      list.push({
        gameId: 'new-game',
        publisherSlug: 'you',
        displayName: 'New Game',
        description: 'Create a new game workspace and connect your coding agent.',
        disabled: false,
        disabledNote: undefined,
        hasConfigure: false,
        thumbnailUrl: '/new-game-thumbnail.jpg',
        owned: false,
      })
    }

    // Every game is configurable: the platform's Host plays setting applies to all.
    const toTile = (g: GameCatalogGame): RoomViewVMNoActiveGameTileVM => {
      if (incompatibleGameIds.value.has(g.gameId)) {
        return {
          gameId: g.gameId,
          publisherSlug: g.publisherSlug,
          displayName: g.displayName,
          description: g.description,
          disabled: true,
          disabledNote: 'Incompatible with API',
          incompatibleApiVersion: true,
          hasConfigure: true,
          thumbnailUrl: g.thumbnailUrl,
          launchChannel: g.launchChannel,
          owned: g.owned,
          developmentProgress: g.developmentProgress,
        }
      }

      // Check if game supports shared screen mode when in shared screen
      const supportsShared = gameRegistry.supportsSharedScreen(g.gameId)
      if (inSharedScreen && !supportsShared) {
        return {
          gameId: g.gameId,
          publisherSlug: g.publisherSlug,
          displayName: g.displayName,
          description: g.description,
          disabled: true,
          disabledNote: 'Not supported in shared screen mode',
          hasConfigure: true,
          thumbnailUrl: g.thumbnailUrl,
          launchChannel: g.launchChannel,
          owned: g.owned,
          developmentProgress: g.developmentProgress,
        }
      }

      const need = Math.max(0, g.minPlayers - seatableMateCount(g.gameId))
      const insufficientMates = need > 0
      // An owned game short on mates is never blocked - selectNoActiveGame applies Omni
      // mode and opens it anyway (see the networked Omni mode decision). A game is also
      // available when Omni mode is already active, including the `test:` URL harness used
      // by browser tests; openGame seats its synthetic mates. Otherwise a non-owned game
      // still refuses to start without enough real mates.
      const disabled = insufficientMates && !g.owned && !mode.isOmniMode()
      return {
        gameId: g.gameId,
        publisherSlug: g.publisherSlug,
        displayName: g.displayName,
        description: g.description,
        disabled,
        disabledNote: disabled ? `Need +${need} ${need === 1 ? 'person' : 'people'}` : undefined,
        insufficientMates,
        hasConfigure: true,
        thumbnailUrl: g.thumbnailUrl,
        launchChannel: g.launchChannel,
        owned: g.owned,
        developmentProgress: g.developmentProgress,
      }
    }

    return arrangeGameTiles({
      all: [...list, ...gameCatalog.games.value.map(toTile)],
      curated: gameCuration.curatedGames.value.map(toTile),
      isCurated: gameCuration.isCurated.value,
      canEdit: gameCuration.canEdit.value,
      showAll: gameCuration.showAll.value,
    })
  })

  // Curated games always lead the list, so the Show all games toggle follows them.
  const curatedCount = computed(() => (gameCuration.isCurated.value ? gameCuration.curatedGames.value.length : 0))

  // In a managed room only hosts start games; everyone else waits for one
  // instead of browsing a catalog they cannot use.
  const waitingMessage = computed(() =>
    roomState.canControlGames.value ? null : `Wait for the ${hostTermInSentence(roomState.hostTerm.value)} to start a game.`
  )
  const noActiveGames = computed<RoomViewVMNoActiveGameTileVM[]>(() => (waitingMessage.value ? [] : catalogTiles.value))

  const noActiveFocusedIndex = signal<number>(0)
  const noActiveGamesPerRow = signal<number>(1)

  // Reset focus index when game list changes.
  effect(() => {
    void noActiveGames.value.length
    noActiveFocusedIndex.value = 0
  })

  // Controller navigation is only active when focus navigation is enabled.
  const isNoActiveGameControllerInputEnabled = computed(() => {
    if (activeGame.value) return false
    if (startSetupOpen.value) return false
    if (configureOpen.value) return false
    if (!focusNavigation.isActive.value) return false
    return true
  })

  // Pointer/mouse interactions should work even when focus navigation is inactive.
  const isNoActiveGamePointerInputEnabled = computed(() => {
    if (activeGame.value) return false
    if (startSetupOpen.value) return false
    if (configureOpen.value) return false
    return true
  })

  function setGamesPerRow(next: number): void {
    noActiveGamesPerRow.value = Number.isFinite(next) && next >= 1 ? Math.floor(next) : 1
  }

  function setFocusedIndex(next: number): void {
    const max = noActiveGames.value.length - 1
    if (max < 0) {
      noActiveFocusedIndex.value = 0
      return
    }
    noActiveFocusedIndex.value = Math.max(0, Math.min(max, next))
  }

  function navigateNoActive(direction: 'up' | 'down' | 'left' | 'right'): void {
    if (!isNoActiveGameControllerInputEnabled.value) return
    if (focusNavigation.focusArea.value !== 'content') return

    const games = noActiveGames.value
    const max = games.length - 1
    if (max < 0) return

    const gamesPerRow = Math.max(1, noActiveGamesPerRow.value)

    const step = (from: number): number => {
      switch (direction) {
        case 'left':
          return from > 0 ? from - 1 : max
        case 'right':
          return from < max ? from + 1 : 0
        case 'up':
          return from >= gamesPerRow ? from - gamesPerRow : from
        case 'down':
          return from < games.length - gamesPerRow ? from + gamesPerRow : from
      }
    }

    let next = step(noActiveFocusedIndex.value)
    // Skip disabled games.
    for (let i = 0; i < games.length; i++) {
      const g = games[next]
      if (!g) break
      if (!g.disabled) break
      next = step(next)
    }

    noActiveFocusedIndex.value = next
  }

  function selectNoActiveGame(gameId: string): void {
    if (!isNoActiveGamePointerInputEnabled.value) {
      return
    }
    const game = noActiveGames.value.find((g) => g.gameId === gameId)
    if (!game || game.disabled) return
    const open = async () => {
      // A creator starting their own game without enough mates applies Omni mode
      // automatically instead of being blocked - see wiki/decisions/2026-10-04-use-
      // networked-rooms-for-creator-omni-mode.md. No game is active/mid-setup yet
      // here, so applyOmniMode() only flips the mode flag (switching this client's
      // local mates to its Omni mates) - opening gameId below is still this
      // function's job.
      if (game.owned && game.insufficientMates) {
        await debugPanelVM.applyOmniMode()
      }
      if (game.launchChannel) {
        await roomActions.setGameChannelPreference(gameId, game.launchChannel)
      }
      // debugPanelVM.openGame (not roomActions.openGame directly) so that Omni mode
      // already being active - e.g. a partner joined after it was turned on - also
      // seats only this client's Omni mates instead of surfacing the assignment dialog.
      await debugPanelVM.openGame(gameId)
    }
    open().catch((e: any) => {
      if (e instanceof Error && e.message.includes('uses unsupported API version')) {
        incompatibleGameIds.value = new Set([...incompatibleGameIds.value, gameId])
        gameNotice.value = incompatibleGameNotice(game.displayName)
        return
      }
      console.error('[PlayAreaVM] openGame failed:', e)
      gameNotice.value = { title: `Can't start ${game.displayName}`, message: e instanceof Error ? e.message : String(e) }
    })
  }

  function reportCurationError(pending: Promise<void>): void {
    pending.catch((e: unknown) => console.error('[PlayAreaVM] curation change failed:', e))
  }

  function showIncompatibleGameDetails(gameId: string): void {
    const game = noActiveGames.value.find((entry) => entry.gameId === gameId)
    if (!game?.incompatibleApiVersion) return
    gameNotice.value = incompatibleGameNotice(game.displayName)
  }

  function closeGameNotice(): void {
    gameNotice.value = null
  }

  function configureNoActiveGame(gameId: string): void {
    if (!isNoActiveGamePointerInputEnabled.value) return
    const game = noActiveGames.value.find((g) => g.gameId === gameId)
    if (!game || !game.hasConfigure) return
    void (gameRegistry.getGameComponentAsync?.(gameId) ?? Promise.resolve(null)).then(() => {
      openConfigure(gameId)
    })
  }

  function selectFocusedNoActive(): void {
    if (!isNoActiveGameControllerInputEnabled.value) return
    if (focusNavigation.focusArea.value !== 'content') return
    const game = noActiveGames.value[noActiveFocusedIndex.value]
    if (!game || game.disabled) return
    selectNoActiveGame(game.gameId)
  }

  function configureFocusedNoActive(): void {
    if (!isNoActiveGameControllerInputEnabled.value) return
    if (focusNavigation.focusArea.value !== 'content') return
    const game = noActiveGames.value[noActiveFocusedIndex.value]
    if (!game || !game.hasConfigure) return
    configureNoActiveGame(game.gameId)
  }

  // Controller input -> game list navigation/selection.
  effect(() => {
    if (!isNoActiveGameControllerInputEnabled.value) return
    if (!focusNavigation.isActive.value) return

    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'navigate') {
        navigateNoActive(event.direction)
      } else if (event.type === 'select') {
        selectFocusedNoActive()
      } else if (event.type === 'configure') {
        configureFocusedNoActive()
      }
    })

    return unsubscribe
  })

  // ===== StartGameSetupDialog =====
  const startSetupPhase = computed<StartGameSetupPhase | null>(() => {
    const setup = startSetup.value
    return setup ? (setup.phase as any) : null
  })

  const slotCountOptions = computed<number[]>(() => {
    const setup = startSetup.value
    if (!setup) return []
    return Array.isArray(setup.slotCountOptions) ? setup.slotCountOptions : []
  })

  const focusedPlayerCountIndex = signal<number>(0)

  effect(() => {
    if (!startSetupOpen.value) return
    if (startSetupPhase.value !== 'choosePlayerCount') return
    // Largest option focused by default.
    focusedPlayerCountIndex.value = Math.max(0, slotCountOptions.value.length - 1)
  })

  async function selectPlayerCount(count: number): Promise<void> {
    if (!startSetup.value) return
    await roomActions.choosePlayerCount(count)
  }

  function selectFocusedPlayerCount(): void {
    const count = slotCountOptions.value[focusedPlayerCountIndex.value]
    if (typeof count === 'number') void selectPlayerCount(count)
  }

  // If there is only one option, skip the count dialog entirely.
  effect(() => {
    if (!startSetupOpen.value) return
    if (startSetupPhase.value !== 'choosePlayerCount') return
    if (slotCountOptions.value.length !== 1) return
    const only = slotCountOptions.value[0]
    if (typeof only !== 'number') return
    void selectPlayerCount(only)
  })

  function cancelStartSetup(): void {
    roomActions.cancelGameSetup()
  }

  // Choose player count navigation (controller input).
  effect(() => {
    if (!startSetupOpen.value) return
    if (startSetupPhase.value !== 'choosePlayerCount') return
    if (!focusNavigation.isActive.value) return

    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'navigate') {
        const idx = focusedPlayerCountIndex.value
        const max = slotCountOptions.value.length - 1
        if (max < 0) return

        const col = idx % 2
        const row = Math.floor(idx / 2)

        let next = idx
        if (event.direction === 'left') {
          if (col > 0) next = idx - 1
        } else if (event.direction === 'right') {
          if (col < 1 && idx + 1 <= max) next = idx + 1
        } else if (event.direction === 'up') {
          if (row > 0) next = idx - 2
        } else if (event.direction === 'down') {
          if (idx + 2 <= max) next = idx + 2
        }

        focusedPlayerCountIndex.value = next
        return
      }

      // "A" should select: treat select as select while dialog open.
      if (event.type === 'select') {
        selectFocusedPlayerCount()
      }

      if (event.type === 'cancel' || event.type === 'exit') {
        cancelStartSetup()
      }
    })

    return unsubscribe
  })

  // Assign slots phase state and navigation.
  const isAssignSlotsPhase = computed(() => startSetupPhase.value === 'assignSlots')
  const groupCount = computed(() => {
    const setup = startSetup.value
    const options = slotCountOptions.value
    const fallback = options[options.length - 1] || 2
    return (setup?.slotCount ?? fallback) || 2
  })

  const mates = computed(() => roomState.mates.value || [])
  const matesById = computed(() => new Map(mates.value.map((m: any) => [m.mateId, m] as const)))

  const selfMateId = computed<string | undefined>(() => {
    return localMatesSystem.omniMates.activeOmniMate.activeLocalOmniMateId.value || localMatesSystem.localMates.localMates.value[0]?.mateId
  })

  function isMateLocallyControlled(mateId: string): boolean {
    const mate = matesById.value.get(mateId)
    if (!mate) return false
    return mate.clientId === getOrGenerateClientId()
  }

  function getMateDisplayName(mateId: string): string {
    return matesById.value.get(mateId)?.name ?? mateId
  }

  const selectedMateId = signal<string | null>(null)
  function setSelectedMateId(next: string | null): void {
    selectedMateId.value = next
  }

  const lastGroupIndex = new Map<string, number>()

  async function setAssignment(mateId: string, assignment: number | 'observer'): Promise<void> {
    if (!startSetup.value) return
    await roomActions.updateMateAssignment(mateId, assignment === 'observer' ? null : assignment)
  }

  async function moveMate(mateId: string, direction: 'up' | 'down' | 'left' | 'right'): Promise<void> {
    if (!mateId) return
    if (!isAssignSlotsPhase.value) return

    const setup = startSetup.value
    if (!setup) return

    const current = setup.assignments[mateId]
    const currentIndex: number | 'observer' = current === undefined ? 'observer' : current

    if (direction === 'down') {
      lastGroupIndex.set(mateId, typeof currentIndex === 'number' ? currentIndex : (lastGroupIndex.get(mateId) ?? 0))
      await setAssignment(mateId, 'observer')
      return
    }

    if (currentIndex === 'observer') {
      if (direction === 'up') {
        const backTo = lastGroupIndex.get(mateId) ?? 0
        await setAssignment(mateId, Math.max(0, Math.min(groupCount.value - 1, backTo)))
      } else if (direction === 'left') {
        await setAssignment(mateId, Math.max(0, groupCount.value - 1))
      } else if (direction === 'right') {
        await setAssignment(mateId, 0)
      }
      return
    }

    if (typeof currentIndex !== 'number') return

    if (direction === 'left' && currentIndex === 0) {
      lastGroupIndex.set(mateId, 0)
      await setAssignment(mateId, 'observer')
      return
    }
    if (direction === 'right' && currentIndex === groupCount.value - 1) {
      lastGroupIndex.set(mateId, groupCount.value - 1)
      await setAssignment(mateId, 'observer')
      return
    }

    if (direction === 'left' || direction === 'right') {
      const delta = direction === 'left' ? -1 : 1
      const next = Math.max(0, Math.min(groupCount.value - 1, currentIndex + delta))
      lastGroupIndex.set(mateId, next)
      await setAssignment(mateId, next)
    }
  }

  // Raw navigation for assignment: map device -> mate and move.
  effect(() => {
    if (!startSetupOpen.value) return
    if (!isAssignSlotsPhase.value) return

    const unsubRaw = controllerInput.subscribeRaw((raw) => {
      const direction = parseDirectionFromRawEvent(raw.rawEvent)
      if (!direction) return

      const mateId =
        raw.deviceId === 'K'
          ? selfMateId.value
          : getMateIdForControllerDeviceId(
              mates.value as any[],
              localMatesSystem.localMates.localMates.value as any[],
              raw.deviceId
            )
      if (!mateId) return
      void moveMate(mateId, direction)
    })

    return unsubRaw
  })

  const isValidArrangement = computed(() => {
    if (!isAssignSlotsPhase.value) return false
    return true
  })

  async function startGameFromSetup(): Promise<void> {
    if (!startSetup.value) return
    if (!isAssignSlotsPhase.value) return
    await roomActions.startGameFromSetup()
  }

  // "A" starts game; cancel closes.
  effect(() => {
    if (!startSetupOpen.value) return
    if (!isAssignSlotsPhase.value) return
    if (!focusNavigation.isActive.value) return

    const unsub = controllerInput.subscribe((event) => {
      if (event.type === 'select') {
        if (isValidArrangement.value) void startGameFromSetup()
      }

      if (event.type === 'cancel' || event.type === 'exit') {
        cancelStartSetup()
      }
    })

    return unsub
  })

  const groups = computed(() => {
    const result: string[][] = Array.from({ length: groupCount.value }, () => [])
    const observers: string[] = []

    const setup = startSetup.value
    const assignments = setup?.assignments || {}

    for (const m of mates.value as any[]) {
      const a = assignments[m.mateId]
      if (a === undefined) {
        observers.push(m.mateId)
      } else if (typeof a === 'number' && result[a]) {
        result[a].push(m.mateId)
      }
    }

    return { slots: result, observers }
  })

  async function assignSelectedToSlot(slotIndex: number): Promise<void> {
    const pid = selectedMateId.value ?? selfMateId.value ?? null
    if (!pid) return
    await setAssignment(pid, slotIndex)
    selectedMateId.value = null
  }

  async function assignSelectedToObservers(): Promise<void> {
    const pid = selectedMateId.value ?? selfMateId.value ?? null
    if (!pid) return
    await setAssignment(pid, 'observer')
    selectedMateId.value = null
  }

  // Close pre-game configure dialog once a game starts.
  effect(() => {
    if (activeGame.value) closeConfigure()
  })

  const availablePlayerCounts = computed<number[]>(() => {
    if (!configureGameId.value) return []
    return gameOptionsDialog.getAvailablePlayerCountsForGame(configureGameId.value)
  })

  const defaultPlayerCount = computed<number>(() => {
    if (!configureGameId.value) return 2
    return gameOptionsDialog.getDefaultPlayerCountForGame(configureGameId.value, mateCount.value)
  })

  const savedOptionsForDialog = computed<GameSavedOptions | null>(() => {
    const id = configureGameId.value
    if (!id) return null
    if (roomState.gameSetup.value?.gameId === id) {
      return roomState.savedOptions.value
    }
    return preGameConfigureSavedOptions.value
  })

  // ===== Pre-game theme input =====

  const preGameThemeInput = signal<any | null>(null)
  let themeRequestId = 0
  effect(() => {
    const game = activeGame.value
    if (game) {
      preGameThemeInput.value = null
      return
    }

    const gameIdForTheme = gameSetup.value?.gameId
    if (!gameIdForTheme) {
      preGameThemeInput.value = null
      return
    }

    const syncTheme = (gameRegistry as any).getGameThemeConfig?.(gameIdForTheme) as any
    if (syncTheme) {
      preGameThemeInput.value = syncTheme
      return
    }

    if (!gameRegistry.getGameThemeConfigAsync) return

    const requestId = ++themeRequestId
    preGameThemeInput.value = null
    void gameRegistry.getGameThemeConfigAsync(gameIdForTheme).then((themeInput: any) => {
      // Only apply if we're still in the start flow for the same game.
      if (requestId !== themeRequestId) return
      if (roomState.activeGame.value) return
      if (roomState.gameSetup.value?.gameId !== gameIdForTheme) return
      preGameThemeInput.value = themeInput
    })
  })

  // ===== Waiting for players overlay =====

  const matesWithAI = computed(() => {
    const mates = roomState.mates.value || []
    return mates.map((m: any) => ({
      ...m,
      isAI: m.isAI || roomState.localAIPlayerIDs.value.has(m.mateId),
    }))
  })

  const totalPlayerCount = computed(() => {
    return matesWithAI.value.filter((m: any) => !String(m.mateId).endsWith('_placeholder')).length
  })

  const requiredPlayerCount = computed(() => {
    const game = activeGame.value
    if (!game) return 2
    const gameId = game.gameId
    // Prefer the game's own configured minPlayers over ctx.numPlayers:
    // resolveSeating (packages/room) pads bgio's player slots up to a
    // hardcoded minimum of 2 regardless of what the game actually needs
    // (a bgio technical requirement, not a gameplay one) — so ctx.numPlayers
    // overstates "required" for any game whose real minPlayers is lower
    // (e.g. 1, for a game that supports solo play), permanently showing
    // "waiting for players" even once the game already has everyone it
    // needs. ctx.numPlayers is only the fallback for the (should-never-
    // happen) case where registry metadata isn't available.
    return (gameId ? gameRegistry.getGameMetadata?.(gameId)?.minPlayers : undefined) ?? game?.ctx?.numPlayers ?? 2
  })

  const supportsBots = computed(() => {
    const gameId = activeGame.value?.gameId
    return gameId ? gameRegistry.supportsBots(gameId) : false
  })

  const needsMorePlayers = computed(() => totalPlayerCount.value < requiredPlayerCount.value)

  // ===== AI thinking =====

  const isCurrentPlayerAI = computed(() => {
    const game = activeGame.value
    if (!game || !game.ctx) return false
    const currentPlayer = game.ctx.currentPlayer
    if (!currentPlayer) return false
    const matesInSlot = game.playerToMateIds?.[parseInt(String(currentPlayer))] || []
    return matesInSlot.some((mateId: string) => roomState.localAIPlayerIDs.value.has(mateId))
  })

  // ===== Actions =====

  function startGame(gameId: string): void {
    selectNoActiveGame(gameId)
  }

  function applyMove(move: any, mateId: string): void {
    roomActions.submitMove(move, mateId).catch((err: any) => {
      console.error('[RoomViewVM] Move submission failed:', err)
    })
  }

  const isManagedRoom = computed(() => Boolean(activeGame.value?.managed))
  const hostMateId = roomState.localHostMateId

  function applyHostMove(move: any): void {
    const mateId = hostMateId.value
    if (!mateId) return
    roomActions.submitHostMove(move, mateId).catch((err: any) => {
      console.error('[RoomViewVM] Host action failed:', err)
    })
  }

  function checkpointGameState(G: unknown): void {
    const basedOnSeqIndex = (activeGame.value?.actions?.length ?? 1) - 1
    // Realtime games never advance ctx themselves (see turn.ts's Stage.NULL
    // idiom — no move ever calls events.endTurn()), so the room's last-known
    // ctx is still correct here. Omitting it (as G alone) would drop ctx
    // entirely: forceGameState stores it as `initialState.ctx`, and
    // JSON.stringify silently strips an `undefined` value — leaving
    // roomConnection.ts's reconstruct() to crash the next time it replays a
    // move through bgio's reducer, which needs a real ctx (e.g. ctx.phase)
    // to run at all.
    const ctx = activeGame.value?.ctx
    roomActions.forceGameState({ G, ctx }, basedOnSeqIndex).catch((err: any) => {
      console.error('[RoomViewVM] Checkpoint submission failed:', err)
    })
  }

  function onGameBoardClick(): void {
    debugPanelVM.requestSwitchToMovesTab()
  }

  // ===== Realtime networked sim (see @merels/sdk's RealtimePlugin) =====
  //
  // Owns the live-ticking local sim for a realtime game in a networked
  // room, so GameContainer.tsx (React) can stay a thin pass-through of
  // GameProps.getLiveGameState — see makeNetworkedSimBinding's header for
  // what it actually does. This lives here rather than in GameContainer
  // because playAreaVM already owns everything the binding needs
  // (activeGame, checkpointGameState, viewingPlayerIndex), and because it
  // means test code driving playAreaVM directly (see the spacewar BDD
  // scenario) exercises the exact same production wiring a real client
  // does, rather than a hand-rolled equivalent.

  let networkedSimBinding: NetworkedSimBinding | null = null
  let realtimeResolveRequestId = 0
  const mySeat = computed<string | undefined>(() => {
    const idx = viewingPlayerIndex.value
    return typeof idx === 'number' ? String(idx) : undefined
  })
  const realtimeGameId = computed(() => activeGame.value?.gameId)
  const realtimeGameVersionId = computed(() => activeGame.value?.gameVersion?.versionId)

  // (Re)creates the binding whenever the game session's identity changes —
  // deliberately narrow dependencies (via the computeds above, plus
  // isNetworked/mySeat), NOT activeGame.value itself, so this does not
  // tear down and re-resolve on every ordinary gameState update (a move or
  // checkpoint broadcast) — only on a genuine game/version/mode/seat
  // change. Room updates instead flow into the existing binding via the
  // separate effect below, exactly like GameContainer's two effects did.
  effect(() => {
    const gameId = realtimeGameId.value
    const versionId = realtimeGameVersionId.value
    const networked = isNetworked.value
    const seat = mySeat.value
    console.log('[diag playAreaVM] RECREATE effect fired:', { gameId, versionId, networked, seat })

    networkedSimBinding?.stop()
    networkedSimBinding = null
    if (!gameId) return

    const gameVersion = activeGame.peek()?.gameVersion
    const requestId = ++realtimeResolveRequestId
    const gameLogicPromise = gameRegistry.getGameLogicAsync?.(gameId, gameVersion) ?? Promise.resolve(null)
    void gameLogicPromise.then((gameLogic) => {
      // Stale: a newer resolution (or a game/version/mode/seat change) has
      // superseded this one — don't clobber whatever it already set up.
      if (requestId !== realtimeResolveRequestId) return
      console.log('[diag playAreaVM] creating binding with mySeat=', seat, 'gameId=', gameId, 'hasRealtime=', Boolean(gameLogic?.realtime))
      networkedSimBinding = makeNetworkedSimBinding({
        isNetworked: networked,
        checkpointState: checkpointGameState,
        gameState: activeGame.peek()?.gameState,
        mySeat: seat,
        realtime: gameLogic?.realtime,
      })
    })
  })

  // Feeds each newly-reconstructed gameState (the room's logged discrete
  // moves, or a checkpoint) into the binding to reconcile against. Safe to
  // call on every activeGame update — the binding ignores a repeated
  // reference, and is a no-op entirely when inactive (not networked, or no
  // realtime plugin resolved yet).
  effect(() => {
    const gs = activeGame.value?.gameState
    console.log('[diag playAreaVM] feed-updates effect fired, hasBinding=', Boolean(networkedSimBinding), 'gameState=', gs)
    networkedSimBinding?.onRoomStateUpdate(gs)
  })

  function getLiveGameState(): unknown {
    return networkedSimBinding?.getLiveGameState?.()
  }

  return {
    isLoading: roomState.isLoading,
    mateCount,
    mode: { isSharedScreen, isOmniMode },
    game: {
      activeGame,
      gameSetup,
      hasActiveGame: computed(() => Boolean(activeGame.value)),
      isStartingGameSetup: computed(() => Boolean(roomState.gameSetup.value)),
      error,
      viewingMateId,
      viewingPlayerIndex,
      getLiveGameState,
      isManagedRoom,
      hostMateId,
    },
    noActiveGame: {
      games: noActiveGames,
      waitingMessage,
      curation: {
        isCurated: gameCuration.isCurated,
        canEdit: gameCuration.canEdit,
        showAll: gameCuration.showAll,
        curatedCount,
      },
      focusedIndex: noActiveFocusedIndex,
      gamesPerRow: noActiveGamesPerRow,
      gameNotice,
      focusNavigation: {
        isActive: focusNavigation.isActive,
        focusArea: focusNavigation.focusArea,
      },
      actions: {
        setGamesPerRow,
        setFocusedIndex,
        navigate: navigateNoActive,
        selectFocused: selectFocusedNoActive,
        configureFocused: configureFocusedNoActive,
        selectGame: selectNoActiveGame,
        showIncompatibleGameDetails,
        closeGameNotice,
        configureGame: configureNoActiveGame,
        toggleShowAllGames: gameCuration.actions.toggleShowAll,
        addCuratedGame: (gameId: string) => reportCurationError(gameCuration.actions.add(gameId)),
        removeCuratedGame: (gameId: string) => reportCurationError(gameCuration.actions.remove(gameId)),
        moveCuratedGame: (gameId: string, toIndex: number) => reportCurationError(gameCuration.actions.move(gameId, toIndex)),
      },
    },
    startGameSetup: {
      isOpen: startSetupOpen,
      phase: startSetupPhase,
      slotCountOptions,
      focusedPlayerCountIndex,
      selectFocusedPlayerCount,
      selectPlayerCount,
      groupCount,
      groups,
      selectedMateId,
      setSelectedMateId,
      assignSelectedToSlot,
      assignSelectedToObservers,
      isValidArrangement,
      startGame: startGameFromSetup,
      cancel: cancelStartSetup,
      selfMateId,
      isMateLocallyControlled,
      getMateDisplayName,
    },
    configureDialog: {
      gameId: configureGameId,
      isOpen: configureOpen,
      availablePlayerCounts,
      defaultPlayerCount,
      savedOptionsForDialog,
      open: openConfigure,
      close: closeConfigure,
    },
    waitingForPlayers: {
      isVisible: computed(() => Boolean(activeGame.value) && activeGame.value?.gameId !== 'new-game' && needsMorePlayers.value),
      currentPlayerCount: totalPlayerCount,
      requiredPlayerCount,
      supportsBots,
    },
    aiTurn: {
      isThinking: computed(() => isCurrentPlayerAI.value && bot.isProcessing.value && !Boolean(activeGame.value?.ctx?.gameover)),
    },
    theme: { preGameThemeInput },
    actions: {
      startGame,
      applyMove,
      applyHostMove,
      checkpointGameState,
      onGameBoardClick,
    },
  }
}
